import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Play, Pause, Heart, Share2, Download, MessageCircle, ArrowLeft } from 'lucide-react';
import DownloadMenu from '../components/DownloadMenu';
import { useToast } from '../hooks/use-toast';
import Logo from '../components/Logo';

export default function SongPage() {
  const { id } = useParams();
  const { t, lang, user, api, current, isPlaying, playSong, togglePlay, toggleLike, liked } = useApp();
  const { toast } = useToast();
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await api.song(id);
        setSong(data.song);
        const c = await api.listComments(id);
        setComments(c.comments || []);
      } catch (e) { setSong(null); }
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [id]);

  const isCurrent = current?.id === song?.id;
  const isLiked = song && liked.includes(song.id);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast({ title: t('share.copied') }); }
    catch { toast({ title: 'Copy failed' }); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!user) return;
    const text = commentText.trim();
    if (!text) return;
    setPosting(true);
    try {
      const data = await api.addComment(id, text);
      setComments(prev => [data.comment, ...prev]);
      setCommentText('');
    } catch (err) { toast({ title: 'Error', description: String(err?.response?.data?.detail || err.message) }); }
    finally { setPosting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-400">Loading...</div>;
  if (!song) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-zinc-300">
      <div className="text-2xl font-display">404</div>
      <div className="text-zinc-500">{lang==='fr' ? 'Chanson introuvable ou privée' : 'Song not found or private'}</div>
      <Link to="/"><Button variant="outline" className="border-white/15 bg-transparent hover:bg-white/5">{lang==='fr'?'Retour':'Back'}</Button></Link>
    </div>
  );

  return (
    <div className="min-h-screen px-4 lg:px-8 py-4 fade-up">
      <div className="flex items-center justify-between mb-6">
        <Link to="/" className="flex items-center gap-2 text-zinc-300 hover:text-white text-sm"><ArrowLeft size={16} /> {lang==='fr'?'Retour':'Back'}</Link>
        <Link to="/"><Logo /></Link>
      </div>

      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
        <div className="relative aspect-square rounded-2xl overflow-hidden border border-white/10">
          <img src={song.cover} alt="" className="w-full h-full object-cover" />
          <button
            onClick={() => isCurrent ? togglePlay() : playSong(song, [song])}
            className="absolute right-4 bottom-4 w-14 h-14 rounded-full bg-white text-black shadow-xl hover:scale-105 transition-transform flex items-center justify-center"
          >
            {isCurrent && isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-1" />}
          </button>
        </div>

        <div>
          <div className="pill mb-2">{song.genre}</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold leading-tight">{song.title[lang] || song.title.en}</h1>
          <div className="text-sm text-zinc-400 mt-1">
            {t('share.by')} {song.ownerId ? (
              <Link to={`/u/${song.ownerId}`} className="text-zinc-200 hover:underline">{song.ownerName || song.artist}</Link>
            ) : <span>{song.artist}</span>}
            <span className="mx-2">·</span>
            <span>{song.plays} {lang==='fr'?'écoutes':'plays'}</span>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={() => isCurrent ? togglePlay() : playSong(song, [song])} className="bg-white text-black hover:bg-zinc-200 gap-2">
              {isCurrent && isPlaying ? <Pause size={16}/> : <Play size={16}/>}
              {isCurrent && isPlaying ? t('player.pause') : t('player.play')}
            </Button>
            <Button variant="outline" onClick={() => toggleLike(song.id)} className={`border-white/15 bg-transparent hover:bg-white/5 gap-2 ${isLiked ? 'text-pink-400' : ''}`}>
              <Heart size={16} fill={isLiked?'currentColor':'none'} /> {song.likes}
            </Button>
            <DownloadMenu song={song} trigger={
              <Button variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 gap-2"><Download size={16}/> {t('player.download')}</Button>
            } />
            <Button variant="outline" onClick={copyLink} className="border-white/15 bg-transparent hover:bg-white/5 gap-2"><Share2 size={16}/> {t('share.copy_link')}</Button>
          </div>

          {song.lyrics && (
            <div className="mt-6 bg-[#101012] border border-white/5 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">{t('player.lyrics')}</div>
              <pre className="whitespace-pre-wrap text-sm text-zinc-200 font-sans">{song.lyrics}</pre>
            </div>
          )}
        </div>
      </div>

      {/* Comments */}
      <div className="max-w-3xl mx-auto mt-12 mb-32">
        <div className="flex items-center gap-2 text-zinc-300 mb-4"><MessageCircle size={18} /> <h2 className="text-lg font-semibold">{t('share.comments')} ({comments.length})</h2></div>
        {user ? (
          <form onSubmit={submitComment} className="flex gap-2 mb-5">
            <Input value={commentText} onChange={(e)=>setCommentText(e.target.value)} placeholder={t('share.comment_ph')} className="bg-[#16161a] border-white/10" />
            <Button type="submit" disabled={posting || !commentText.trim()} className="bg-white text-black hover:bg-zinc-200">{t('share.send')}</Button>
          </form>
        ) : (
          <div className="text-sm text-zinc-500 mb-5"><Link to="/auth?mode=signin" className="text-zinc-200 hover:underline">{t('share.sign_in_to_comment')}</Link></div>
        )}
        <div className="space-y-3">
          {comments.map(c => (
            <div key={c.id} className="flex gap-3 p-3 rounded-lg bg-[#101012] border border-white/5">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 via-orange-400 to-yellow-300 flex items-center justify-center text-black font-bold text-sm shrink-0">
                {(c.userName || 'U')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-sm">
                  <Link to={`/u/${c.userId}`} className="text-zinc-200 font-medium hover:underline">{c.userName}</Link>
                  <span className="text-xs text-zinc-500">{new Date(c.createdAt).toLocaleString()}</span>
                </div>
                <div className="text-sm text-zinc-300 mt-0.5 whitespace-pre-wrap break-words">{c.text}</div>
              </div>
            </div>
          ))}
          {!comments.length && <div className="text-sm text-zinc-500 text-center py-6">{lang==='fr'?'Aucun commentaire pour le moment':'No comments yet'}</div>}
        </div>
      </div>
    </div>
  );
}
